import { CSSProperties } from 'react';

const MIN_RENDERED_SIZE = 1;
const DIVIDER_COLOR = 'var(--color-border-default, rgba(0, 0, 0, 0.2))';

export interface ContainerInfo {
    minX: number;
    minY: number;
    maxX: number;
    maxY: number;
    width: number;
    height: number;
    aspectRatio: number;
}

export interface LayoutMetrics {
    containerStyle: CSSProperties;
    scale: number;
}

interface BottomOriginStyleParams {
    x: number;
    y: number;
    width: number;
    height: number;
    minX: number;
    maxY: number;
    scale: number;
    topInsetRatio?: number;
    showDivider?: boolean;
    dividerColor?: string;
    overflow?: CSSProperties['overflow'];
}

const getValidDimension = (dimension: number) => (Number.isFinite(dimension) && dimension > 0 ? dimension : MIN_RENDERED_SIZE);

export const calculateLayoutMetrics = (containerInfo: ContainerInfo, availableWidth: number, availableHeight: number): LayoutMetrics => {
    const layoutWidth = getValidDimension(containerInfo.width);
    const layoutHeight = getValidDimension(containerInfo.height);
    const widthScale = availableWidth > 0 ? availableWidth / layoutWidth : 1;
    const heightScale = availableHeight > 0 ? availableHeight / layoutHeight : 1;
    const scale = Math.max(Math.min(widthScale, heightScale), Number.EPSILON);

    return {
        scale,
        containerStyle: {
            position: 'relative',
            width: `${layoutWidth * scale}px`,
            height: `${layoutHeight * scale}px`
        }
    };
};

export const getBottomOriginItemStyle = (params: BottomOriginStyleParams): CSSProperties => {
    const width = getValidDimension(params.width) * params.scale;
    const height = getValidDimension(params.height) * params.scale;
    const topInset = height * (params.topInsetRatio ?? 0);
    const showDivider = params.showDivider ?? true;

    return {
        position: 'absolute',
        left: `${(params.x - params.minX) * params.scale}px`,
        top: `${(params.maxY - params.y - params.height) * params.scale + topInset}px`,
        width: `${Math.max(width, MIN_RENDERED_SIZE)}px`,
        height: `${Math.max(height - topInset, MIN_RENDERED_SIZE)}px`,
        boxSizing: 'border-box',
        overflow: params.overflow ?? 'hidden',
        ...(showDivider
            ? {
                  outline: `1px solid ${params.dividerColor ?? DIVIDER_COLOR}`,
                  outlineOffset: '-1px'
              }
            : {})
    };
};
